import { CitationBadge } from './CitationBadge.tsx';

type Precedent = {
  teamId: number;
  sharedSpecies: number;
  placement: number | null;
  playerHandle: string | null;
  tournamentName: string | null;
  tournamentDate: string | null;
  sourceUrl: string | null;
};

type Result = {
  precedents: Precedent[];
  teams_considered: number;
  teams_matched: number;
};

// Surfaces tournament entries whose team composition matches the matchup
// query. Used by the VerdictBlock to cite real placements ("Loureiro 2nd
// OCIC EU 2026-03-22"). Always includes source_url for verification.
export function TournamentPrecedentsCard({ data }: { data: Result }) {
  if (!data || !Array.isArray(data.precedents) || data.precedents.length === 0) {
    return (
      <div
        className="mono"
        style={{
          padding: '12px 14px',
          border: '1px dashed var(--line-2)',
          borderRadius: 4,
          color: 'var(--fg-3)',
          fontSize: 12,
          textAlign: 'center',
        }}
      >
        no tournament precedents found for this matchup
      </div>
    );
  }
  return (
    <div className="vb-card">
      <div className="vb-card-header">
        <span
          className="caps"
          style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
        >
          tournament precedents
        </span>
        <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
          {data.teams_matched} matched · {data.teams_considered} considered
        </span>
      </div>
      <div>
        {data.precedents.map((p, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: same team can have multiple entries (different events)
            key={`${p.teamId}-${i}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: 10,
              alignItems: 'center',
              padding: '8px 12px',
              borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            }}
          >
            <div
              className="mono tabular"
              style={{
                color: 'var(--accent-2)',
                fontSize: 14,
                fontWeight: 500,
                minWidth: 32,
                textAlign: 'right',
              }}
            >
              {p.placement !== null ? `#${p.placement}` : '—'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: 'var(--fg-1)',
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p.playerHandle ?? 'unknown player'}
              </div>
              <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 10 }}>
                {p.tournamentName ?? 'unnamed tournament'}
                {p.tournamentDate && (
                  <span style={{ color: 'var(--fg-4)', marginLeft: 6 }}>· {p.tournamentDate}</span>
                )}
                <span style={{ color: 'var(--fg-4)', marginLeft: 6 }}>
                  · {p.sharedSpecies}/6 shared
                </span>
              </div>
            </div>
            {p.sourceUrl && <CitationBadge source="limitless" suffix="↗" href={p.sourceUrl} />}
          </div>
        ))}
      </div>
    </div>
  );
}
